import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/LOGO SnapED.png';
import {
    Menu,
    Home,
    Info,
    BookOpen,
    LogIn,
    UserPlus,
    Briefcase
} from 'lucide-react';
import { Button } from './ui/button';
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    navigationMenuTriggerStyle,
} from './ui/navigation-menu';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from './ui/sheet';
import { cn } from '../lib/utils';

const LandingNavbar = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const navLinks = [
        { name: 'Home', path: '/', icon: <Home className="h-4 w-4" /> },
        { name: 'Opportunities', path: '/job-portal/opportunities', icon: <Briefcase className="h-4 w-4" /> },
        { name: 'CodeCampus', path: '/tech-library', icon: <BookOpen className="h-4 w-4" /> },
        { name: 'About', path: '/about', icon: <Info className="h-4 w-4" /> },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b bg-background/80 backdrop-blur-xl border-border/40">
            <div className="container flex h-full items-center justify-between px-4 max-w-7xl mx-auto">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2">
                    <img src={logo} alt="SnapED" className="h-8 w-auto md:h-9" />
                </Link>

                {/* Desktop Nav */}
                <NavigationMenu className="hidden md:flex">
                    <NavigationMenuList className="gap-2">
                        {navLinks.map((link) => (
                            <NavigationMenuItem key={link.path}>
                                <Link to={link.path}>
                                    <NavigationMenuLink
                                        className={cn(
                                            navigationMenuTriggerStyle(),
                                            "bg-transparent hover:bg-accent/50",
                                            isActive(link.path) && "text-primary font-bold bg-accent/20"
                                        )}
                                    >
                                        {link.name}
                                    </NavigationMenuLink>
                                </Link>
                            </NavigationMenuItem>
                        ))}
                    </NavigationMenuList>
                </NavigationMenu>

                {/* Actions */}
                <div className="hidden md:flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/login')}
                        className="rounded-full px-5"
                    >
                        Sign In
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => navigate('/signup')}
                        className="rounded-full px-5 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20"
                    >
                        Sign Up
                    </Button>
                </div>

                {/* Mobile Menu Toggle */}
                <div className="md:hidden flex items-center gap-2">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-foreground">
                                <Menu className="h-6 w-6" />
                                <span className="sr-only">Toggle menu</span>
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[300px] sm:w-[350px] bg-background border-l border-border/40">
                            <SheetHeader className="mb-8">
                                <SheetTitle className="text-left flex items-center gap-2">
                                    <img src={logo} alt="SnapED" className="h-8 w-auto" />
                                </SheetTitle>
                            </SheetHeader>
                            <nav className="flex flex-col gap-4">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={cn(
                                            "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold transition-colors",
                                            isActive(link.path)
                                                ? "bg-primary/10 text-primary"
                                                : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                        )}
                                    >
                                        {link.icon}
                                        {link.name}
                                    </Link>
                                ))}
                                <div className="mt-8 flex flex-col gap-3 pt-8 border-t border-border/40">
                                    <Button
                                        variant="outline"
                                        onClick={() => navigate('/login')}
                                        className="w-full rounded-xl h-12 text-base font-semibold"
                                    >
                                        <LogIn className="mr-2 h-5 w-5" /> Sign In
                                    </Button>
                                    <Button
                                        onClick={() => navigate('/signup')}
                                        className="w-full rounded-xl h-12 text-base font-bold bg-primary hover:bg-primary/90 h-12"
                                    >
                                        <UserPlus className="mr-2 h-5 w-5" /> Sign Up
                                    </Button>
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default LandingNavbar;
